import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { Bubble } from 'react-chartjs-2';

import Img from '../../../../Img';
import { GameCardBody } from '../GameCardTooltip';
import {
    InfoTrigger,
    LegendSheet,
    ScatterLegendList,
    ConsistencyInfoBody,
} from '../ScatterSection/scatterInfo';
import {
    buildScatterData,
    buildScatterOptions,
    buildScatterMidlinePlugin,
    mapRotatedClientPoint,
    findScatterPointIndexAt,
} from '../performanceStatsUtils';
import styles from '../performanceStats.scss';

const getViewportSize = () => ({
    width: window.innerWidth,
    height: window.innerHeight,
});

const LandscapeScatterOverlay = ({
    singles,
    currentUtr,
    consistency,
    entries,
    selectedIndex,
    onSelect,
    onClose,
    'data-testid': dataTestId,
}) => {
    const chartRef = useRef(null);
    const cardRefs = useRef({});
    const [viewport, setViewport] = useState(getViewportSize);
    const [showLegend, setShowLegend] = useState(false);
    const [showConsistencyInfo, setShowConsistencyInfo] = useState(false);

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, []);

    useEffect(() => {
        const handleResize = () => setViewport(getViewportSize());
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape' && !showLegend && !showConsistencyInfo) {
                onClose();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onClose, showLegend, showConsistencyInfo]);

    useEffect(() => {
        const selectedCardNode = cardRefs.current[selectedIndex];
        if (selectedCardNode && typeof selectedCardNode.scrollIntoView === 'function') {
            selectedCardNode.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
    }, [selectedIndex]);

    const selectedPointIndex = entries[selectedIndex]?.pointIndex ?? null;
    const scatterData = useMemo(
        () => buildScatterData(singles, selectedPointIndex),
        [singles, selectedPointIndex],
    );
    const scatterOptions = useMemo(
        () => buildScatterOptions(singles, currentUtr, null, true),
        [singles, currentUtr],
    );
    const scatterPlugins = useMemo(
        () => [buildScatterMidlinePlugin(currentUtr, true)],
        [currentUtr],
    );

    const handleChartTap = useCallback((event) => {
        const chart = chartRef.current?.chartInstance;
        if (!chart?.canvas) { return; }
        const canvasRect = chart.canvas.getBoundingClientRect();
        const position = mapRotatedClientPoint(event.clientX, event.clientY, canvasRect);
        const pointModels = (chart.getDatasetMeta(0)?.data || []).map(element => element?._model);
        const pointIndex = findScatterPointIndexAt(pointModels, position);
        if (pointIndex === -1) { return; }
        const cardIndex = entries.findIndex(entry => entry.pointIndex === pointIndex);
        if (cardIndex !== -1) {
            onSelect(cardIndex);
        }
    }, [entries, onSelect]);

    if (typeof document === 'undefined') { return null; }

    const rotatorStyle = {
        width: `${viewport.height}px`,
        height: `${viewport.width}px`,
    };

    return ReactDOM.createPortal(
        <div className={styles.landscapeOverlay} data-testid={dataTestId}>
            <div className={styles.landscapeRotator} style={rotatorStyle}>
                <div className={styles.landscapeChartPanel}>
                    <div className={`${styles.chartHeaderRow} ${styles.landscapeHeaderRow}`} data-testid={dataTestId && `${dataTestId}.consistency`}>
                        <span className={styles.chartHeaderTitleGroup}>
                            <span className={styles.chartHeaderLabel} data-testid={dataTestId && `${dataTestId}.consistency.label`}>Performance Consistency</span>
                            <InfoTrigger onOpen={() => setShowConsistencyInfo(true)} data-testid={dataTestId && `${dataTestId}.consistency.info-btn`} />
                        </span>
                        {consistency && (
                            <React.Fragment>
                                <span className={styles.chartHeaderValue} data-testid={dataTestId && `${dataTestId}.consistency.value`}>{consistency.value}</span>
                                {consistency.range && (
                                    <span className={styles.chartHeaderRange} data-testid={dataTestId && `${dataTestId}.consistency.range`}>({consistency.range})</span>
                                )}
                            </React.Fragment>
                        )}
                    </div>
                    <div
                        className={styles.landscapeChartCanvas}
                        onClick={handleChartTap}
                        data-testid={dataTestId && `${dataTestId}.chart`}
                    >
                        {/* Keyed so the mount-only plugins prop re-installs the
                            midline plugin if currentUtr changes while open. */}
                        <Bubble key={`landscape-scatter-${currentUtr ?? 'none'}`} ref={chartRef} data={scatterData} options={scatterOptions} plugins={scatterPlugins} />
                    </div>
                    <div className={styles.landscapeFooterRow}>
                        <button
                            type="button"
                            className={styles.legendTriggerBtn}
                            onClick={() => setShowLegend(true)}
                            data-testid={dataTestId && `${dataTestId}.legend-trigger`}
                        >
                            Legend
                        </button>
                        <button
                            type="button"
                            className={styles.expandTriggerBtn}
                            onClick={onClose}
                            data-testid={dataTestId && `${dataTestId}.collapse-btn`}
                        >
                            <Img src="/img/icons/rotate-to-portrait-icon.svg" alt="Back to portrait" className={styles.expandTriggerIcon} data-testid={dataTestId && `${dataTestId}.collapse-btn.icon`} />
                        </button>
                    </div>
                </div>
                <div className={styles.landscapeCardColumn} data-testid={dataTestId && `${dataTestId}.cards`}>
                    {entries.map((entry, entryIndex) => {
                        const cardKey = entry.card.id ?? entryIndex;
                        const isSelected = entryIndex === selectedIndex;
                        return (
                            <div
                                key={`landscape-card-${cardKey}`}
                                ref={(node) => { cardRefs.current[entryIndex] = node; }}
                                className={`${styles.landscapeCard} ${isSelected ? styles.landscapeCardSelected : ''}`}
                                onClick={() => onSelect(entryIndex)}
                                data-testid={dataTestId && `${dataTestId}.cards.card-${cardKey}`}
                            >
                                <GameCardBody card={entry.card} data-testid={dataTestId && `${dataTestId}.cards.card-${cardKey}`} />
                            </div>
                        );
                    })}
                </div>
                <LegendSheet
                    inline
                    isOpen={showLegend}
                    onClose={() => setShowLegend(false)}
                    data-testid={dataTestId && `${dataTestId}.legend`}
                >
                    <ScatterLegendList horizontal data-testid={dataTestId && `${dataTestId}.legend`} />
                </LegendSheet>
                <LegendSheet
                    inline
                    isOpen={showConsistencyInfo}
                    onClose={() => setShowConsistencyInfo(false)}
                    data-testid={dataTestId && `${dataTestId}.consistency-info`}
                >
                    <div className={styles.legendSheetTitle} data-testid={dataTestId && `${dataTestId}.consistency-info.title`}>About Performance Consistency</div>
                    <div className={styles.infoContent} data-testid={dataTestId && `${dataTestId}.consistency-info.content`}>
                        <ConsistencyInfoBody data-testid={dataTestId && `${dataTestId}.consistency-info`} />
                    </div>
                </LegendSheet>
            </div>
        </div>,
        document.body,
    );
};

LandscapeScatterOverlay.propTypes = {
    singles: PropTypes.shape({
        scatterplot: PropTypes.shape({
            data: PropTypes.arrayOf(PropTypes.shape({})),
        }),
    }),
    currentUtr: PropTypes.number,
    consistency: PropTypes.shape({
        value: PropTypes.string,
        range: PropTypes.string,
    }),
    entries: PropTypes.arrayOf(PropTypes.shape({
        pointIndex: PropTypes.number,
        card: PropTypes.shape({}),
    })),
    selectedIndex: PropTypes.number,
    onSelect: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    'data-testid': PropTypes.string,
};

LandscapeScatterOverlay.defaultProps = {
    singles: null,
    currentUtr: null,
    consistency: null,
    entries: [],
    selectedIndex: 0,
    'data-testid': null,
};

export default LandscapeScatterOverlay;
